import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2, CheckCircle, AlertCircle, Mail, RefreshCw } from 'lucide-react';
import { supabase } from '@/lib/supabase/client';
import { toast } from 'sonner';
import { verifyEmailToken } from '@/lib/api/auth-emails';

type VerificationStatus = 'loading' | 'success' | 'error' | 'no-token' | 'resend';

export default function VerifyEmailPage() {
  const [status, setStatus] = useState<VerificationStatus>('loading');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [resendEmail, setResendEmail] = useState<string>('');
  const [isResending, setIsResending] = useState(false);
  const [countdown, setCountdown] = useState(5);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const verifyEmail = async () => {
      try {
        // Supabase sends verification tokens in different ways:
        // 1. Hash fragment: #access_token=...&type=signup (auto-handled by Supabase client)
        // 2. Query params: ?token=...&type=signup (for email verification links)
        // 3. Query params: ?token_hash=...&type=email (for OTP verification)

        const hash = window.location.hash;
        const tokenHash = searchParams.get('token_hash');
        const token = searchParams.get('token');
        const type = searchParams.get('type');

        console.log('[Email Verify] Checking verification params:', {
          hasHash: !!hash,
          hasTokenHash: !!tokenHash,
          hasToken: !!token,
          type
        });

        // If there's a hash fragment, Supabase client handles it automatically
        // via onAuthStateChange. We just need to wait for the session.
        if (hash && hash.includes('access_token')) {
          console.log('[Email Verify] Hash fragment detected, waiting for Supabase to process...');
          
          // Wait a moment for Supabase to process the hash
          await new Promise(resolve => setTimeout(resolve, 1000));
          
          const { data: { session }, error: sessionError } = await supabase.auth.getSession();
          
          if (sessionError) {
            throw sessionError;
          }
          
          if (session) {
            console.log('[Email Verify] Session established from hash fragment');
            setStatus('success');
            return;
          }
        }

        // Handle token_hash verification (OTP style)
        if (tokenHash && type) {
          console.log('[Email Verify] Verifying with token_hash...');
          await verifyEmailToken(tokenHash, type);
          console.log('[Email Verify] Verification successful via API token_hash');
          setStatus('success');
          return;
        }

        // Handle token verification (older style)
        if (token && type) {
          console.log('[Email Verify] Verifying with token...');
          await verifyEmailToken(token, type);
          console.log('[Email Verify] Verification successful via API token');
          setStatus('success');
          return;
        }

        // Check if user is already verified (maybe clicked link twice)
        const { data: { session } } = await supabase.auth.getSession();
        if (session?.user?.email_confirmed_at) {
          console.log('[Email Verify] User already verified');
          setStatus('success');
          return;
        }

        // No valid token found
        console.log('[Email Verify] No valid verification token found');
        setStatus('no-token');

      } catch (error: unknown) {
        console.error('[Email Verify] Verification error:', error);
        
        const errorMsg = error instanceof Error ? error.message : 'Verification failed';
        
        // Handle specific error cases
        if (errorMsg.includes('expired') || errorMsg.includes('invalid')) {
          setErrorMessage('This verification link has expired or is invalid. Please request a new one.');
        } else if (errorMsg.includes('already confirmed') || errorMsg.includes('already registered')) {
          // User is already verified, treat as success
          setStatus('success');
          return;
        } else {
          setErrorMessage(errorMsg);
        }
        
        setStatus('error');
      }
    };

    verifyEmail();
  }, [searchParams]);

  // Countdown for redirect after success
  useEffect(() => {
    if (status === 'success') {
      const timer = setInterval(() => {
        setCountdown(prev => {
          if (prev <= 1) {
            clearInterval(timer);
            navigate('/auth/signin');
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [status, navigate]);

  const handleResendVerification = async () => {
    if (!resendEmail) {
      toast.error('Please enter your email address');
      return;
    }

    setIsResending(true);

    try {
      const { error } = await supabase.auth.resend({
        type: 'signup',
        email: resendEmail,
        options: {
          emailRedirectTo: `${window.location.origin}/auth/verify`
        }
      });

      if (error) {
        throw error;
      }

      toast.success('Verification email sent! Please check your inbox.');
      setStatus('resend');
    } catch (error: unknown) {
      console.error('[Email Verify] Resend error:', error);
      const errorMsg = error instanceof Error ? error.message : 'Failed to resend verification email';
      toast.error(errorMsg);
    } finally {
      setIsResending(false);
    }
  };

  // Loading state
  if (status === 'loading') {
    return (
      <div className="container flex h-screen w-screen flex-col items-center justify-center px-4">
        <div className="mx-auto flex w-full flex-col justify-center space-y-6 sm:w-[400px]">
          <Card>
            <CardContent className="pt-6">
              <div className="flex flex-col items-center justify-center space-y-4 py-6 text-center">
                <Loader2 className="h-12 w-12 animate-spin text-primary" />
                <div className="space-y-2">
                  <h3 className="text-lg font-semibold">Verifying your email...</h3>
                  <p className="text-sm text-muted-foreground">
                    Please wait while we confirm your email address.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Success state
  if (status === 'success') {
    return (
      <div className="container flex h-screen w-screen flex-col items-center justify-center px-4">
        <div className="mx-auto flex w-full flex-col justify-center space-y-6 sm:w-[400px]">
          <Card>
            <CardContent className="pt-6">
              <div className="flex flex-col items-center justify-center space-y-4 py-6 text-center">
                <CheckCircle className="h-12 w-12 text-green-500" />
                <div className="space-y-2">
                  <h3 className="text-lg font-semibold">Email Verified!</h3>
                  <p className="text-sm text-muted-foreground">
                    Your email has been successfully verified. You can now sign in to your account.
                  </p>
                  <p className="text-sm text-muted-foreground">
                    Redirecting to sign in in {countdown} seconds...
                  </p>
                </div>
                <Button onClick={() => navigate('/auth/signin')} className="mt-4">
                  Sign In Now
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Resend confirmation state
  if (status === 'resend') {
    return (
      <div className="container flex h-screen w-screen flex-col items-center justify-center px-4">
        <div className="mx-auto flex w-full flex-col justify-center space-y-6 sm:w-[400px]">
          <Card>
            <CardContent className="pt-6">
              <div className="flex flex-col items-center justify-center space-y-4 py-6 text-center">
                <Mail className="h-12 w-12 text-primary" />
                <div className="space-y-2">
                  <h3 className="text-lg font-semibold">Check Your Email</h3>
                  <p className="text-sm text-muted-foreground">
                    We&apos;ve sent a new verification link to <strong>{resendEmail}</strong>.
                    Please check your inbox and spam folder.
                  </p>
                </div>
                <div className="flex flex-col gap-2 w-full mt-4">
                  <Button variant="outline" onClick={() => setStatus('no-token')}>
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Send to Different Email
                  </Button>
                  <Button variant="ghost" onClick={() => navigate('/auth/signin')}>
                    Back to Sign In
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Error state or no-token state (show resend form)
  return (
    <div className="container flex h-screen w-screen flex-col items-center justify-center px-4">
      <div className="mx-auto flex w-full flex-col justify-center space-y-6 sm:w-[400px]">
        <Card>
          <CardContent className="pt-6">
            <div className="flex flex-col items-center justify-center space-y-4 py-6 text-center">
              <AlertCircle className={`h-12 w-12 ${status === 'error' ? 'text-destructive' : 'text-yellow-500'}`} />
              <div className="space-y-2">
                <h3 className="text-lg font-semibold">
                  {status === 'error' ? 'Verification Failed' : 'Verify Your Email'}
                </h3>
                <p className="text-sm text-muted-foreground">
                  {status === 'error' 
                    ? errorMessage || 'We couldn\'t verify your email. The link may have expired.'
                    : 'Enter your email address to receive a new verification link.'
                  }
                </p>
              </div>

              <div className="w-full space-y-4 mt-4">
                <div className="space-y-2">
                  <Label htmlFor="email">Email Address</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="your@email.com"
                    value={resendEmail}
                    onChange={(e) => setResendEmail(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        handleResendVerification();
                      }
                    }}
                  />
                </div>
                
                <Button 
                  onClick={handleResendVerification} 
                  disabled={isResending || !resendEmail}
                  className="w-full"
                >
                  {isResending ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Mail className="h-4 w-4 mr-2" />
                      Send Verification Email
                    </>
                  )}
                </Button>

                <Button variant="ghost" onClick={() => navigate('/auth/signin')} className="w-full">
                  Back to Sign In
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
